import { CSRF_COOKIE } from './authConstants';

const CSRF_STORAGE_KEY = 'flashai_csrf';

/**
 * Persist CSRF token from auth responses.
 *
 * Stored in both localStorage (shared across tabs/new windows on the same origin)
 * and sessionStorage (per-tab fallback), because cross-subdomain SPAs
 * (testai.* -> testapi.*) cannot read API-scoped cookies from document.cookie.
 */
export function setCsrfToken(token: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {
    // ignore quota / private mode
  }
  try {
    sessionStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {
    // ignore quota / private mode
  }
}

export function clearCsrfToken(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(CSRF_STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(CSRF_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function captureCsrfFromAuthPayload(data: unknown): void {
  if (
    data &&
    typeof data === 'object' &&
    typeof (data as { csrf_token?: unknown }).csrf_token === 'string'
  ) {
    setCsrfToken((data as { csrf_token: string }).csrf_token);
  }
}

/** Read double-submit CSRF token for cookie-authenticated refresh/logout. */
export function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;

  try {
    const local = localStorage.getItem(CSRF_STORAGE_KEY);
    if (local) return local;
  } catch {
    // ignore
  }

  try {
    const session = sessionStorage.getItem(CSRF_STORAGE_KEY);
    if (session) return session;
  } catch {
    // ignore
  }

  const prefix = `${CSRF_COOKIE}=`;
  const cookies = document.cookie.split(';');
  for (const raw of cookies) {
    const cookie = raw.trim();
    if (cookie.startsWith(prefix)) {
      return decodeURIComponent(cookie.slice(prefix.length));
    }
  }
  return null;
}

export function csrfHeaders(): Record<string, string> {
  const csrf = getCsrfToken();
  return csrf ? { 'X-CSRF-Token': csrf } : {};
}
